import { DataSet, Network } from "vis-network";
import { Edge } from "./types";
import { GraphType } from "./graphType";

export const filterRelationshipsByDate = (
  edges: Edge[],
  selectedDate: string | null,
  activeRelationshipsOnly: boolean,
): Edge[] => {
  if (!selectedDate && !activeRelationshipsOnly) return edges;

  const date = selectedDate || new Date().toISOString().slice(0, 10);
  const isActive = (edge: Edge): boolean => {
    const startDate = edge.properties.start_date?.slice(0, 10);
    const endDate = edge.properties.end_date?.slice(0, 10);

    // A relationship is active from its start date until its end date.
    if (startDate && startDate > date) return false;
    if (endDate && endDate <= date) return false;

    // A known end date lets us reconstruct history despite today's inactive status.
    // Without an end date, keep an explicit inactive status instead of reviving the edge.
    if (selectedDate && endDate) return true;
    return edge.properties.is_active !== false;
  };

  return edges.filter(edge => {
    const startDate = edge.properties.start_date?.slice(0, 10);
    if (startDate && startDate > date) return false;
    return !activeRelationshipsOnly || isActive(edge);
  }).map(edge => ({
    ...edge,
    properties: {
      ...edge.properties,
      is_active: isActive(edge),
      // Hide future end dates in the historical view without changing the source data.
      end_date: selectedDate && edge.properties.end_date && edge.properties.end_date.slice(0, 10) > date
        ? undefined
        : edge.properties.end_date,
    },
  }));
};

export const getEdgesForGraphType = (graphTypes: string[], edges: Edge[]): Edge[] => {
  // Translate graph types to relationship types
  const relationshipTypes = graphTypes.map(GraphType.getRelationshipType);

  return edges.filter((edge) => {
    // We want to find edges which are reachable from the source node via relationships selected
    // by the current filters. Edges can be present on multiple paths. The cumulative relationship
    // types for each path are stored in a list.

    // For each path an edge exists on, check if the current filters include the
    // cumulative relationship types. If any such path exists, then the edge is considered visible.
    return edge.metadata.cumulativeRelationshipTypes.some(pathCumulativeRelationships => {
      return pathCumulativeRelationships.every(relationshipType => {
        return relationshipTypes.includes(relationshipType);
      });
    });
  });
};

export const filterNodesForGraphType = (graphTypes: string[], edges: DataSet<Edge>, network: Network): Set<string> => {
  var filteredNodes: Set<string> = new Set();

  var graphEdges = getEdgesForGraphType(graphTypes, edges.get());

  // get the nodes from each end of the filtered edges
  graphEdges.forEach(edge => {
    const connectedNodes: any = network.getConnectedNodes(edge.id);
    connectedNodes.forEach((fromTo: any) => {
      filteredNodes.add(fromTo);
    });
  });

  return filteredNodes;
}

/**
 * Computes which of a set of nodes that are reachable from a given source node in a network,
 * while excluding certain nodes from the traversal.
 *
 * @param nodes - The nodes to include in the traversal.
 * @param network - The network object that provides connectivity information between nodes.
 * @param sourceNodeId - The ID of the source node from which the traversal begins.
 * @param excludeNodeId - The ID of a node to exclude from the traversal.
 * @returns A set of node IDs that are reachable from the source node, excluding the specified node (and its network).
 */
export const getReachableNodesFromSource = (nodes: Set<string>, network: Network, sourceNodeId: string, excludeNodeId: string): Set<string> => {
  const reachableNodes = new Set<string>();
  const queue: string[] = [sourceNodeId];

  while (queue.length > 0) {
    const currentNodeId = queue.shift()!;
    reachableNodes.add(currentNodeId);
    const connectedNodes = network.getConnectedNodes(currentNodeId) as string[];

    for (const nodeId of connectedNodes) {
      if (!reachableNodes.has(nodeId) && nodes.has(nodeId) && nodeId !== excludeNodeId) {
        queue.push(nodeId);
      }
    }
  }

  return reachableNodes;
}
